import React, {useEffect, useState} from 'react';

import {useSelector} from 'react-redux';

import {CommentListItems} from 'app/comments/components/CommentListItems';
import {PaginatedList} from 'app/general/widgets/PaginatedList';

import {type CommentFetchType, useComments} from '../api';
import {selectIsHidingDeletedComments} from '../slice';

import type {FilterContentType} from '../types';
import type {CommentSerializedType, ListResponseType} from 'sweet-types';

interface Props {
  noResultMessage: JSX.Element;
  canDelete: boolean;
  objectId: number;
  contentType: FilterContentType;
}

export const CommentList = ({
  objectId,
  canDelete,
  contentType,
  noResultMessage,
}: Props): JSX.Element => {
  const [firstElementId, setFirstElementId] = useState<number>(-1);

  const isHidingDeletedComments = useSelector(selectIsHidingDeletedComments);

  useEffect(() => {
    scrollToCommentInURL();
  }, []);

  useEffect(() => {
    if (firstElementId !== -1) {
      scrollToCommentInURL();
    }
  }, [firstElementId]);

  const scrollToCommentInURL = () => {
    // small hack to scroll into a comment if it's in the
    // URL fragment identifier. we need this because the comment
    // are not in the page when it's loaded so we need force the scroll
    // after it's loaded
    const commentId = getTargetComment();
    if (commentId != null) {
      scrollToComment(commentId);
    }
  };

  const getTargetComment = (): number | undefined => {
    const {hash} = location;
    if (hash !== undefined) {
      const match = hash.match(/comment(\d+)/);
      if (match != null) {
        return parseInt(match[1], 10);
      }
    }
  };

  const scrollToComment = (id: number) => {
    const commentNode = document.querySelector(`#comment${id}`);
    if (commentNode != null) {
      commentNode.scrollIntoView({behavior: 'smooth'});
    }
  };

  const targetCommentId = getTargetComment();

  const renderComment = (data: ListResponseType<CommentSerializedType>) => {
    if (
      data?.count > 0 &&
      data?.previous == null &&
      firstElementId !== data.results[0].id
    ) {
      setFirstElementId(data.results[0].id);
    }
    return data == null ? null : data?.count === 0 ? (
      noResultMessage
    ) : (
      <CommentListItems
        data={data}
        canDelete={canDelete}
        targetCommentId={targetCommentId}
        contentType={contentType}
      />
    );
  };
  return (
    <div className="mb-2">
      <PaginatedList<CommentFetchType, CommentSerializedType>
        hook={useComments}
        params={{
          object_pk: objectId,
          content_type: contentType,
          exclude_deleted_comments: isHidingDeletedComments ? '1' : undefined,
        }}
        render={renderComment}
      />
    </div>
  );
};
